import { MapPin, Phone, Mail, Globe, Satellite, Building2 } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { Icon } from "@/components/Icon";
import { CONTACT } from "@/data/site";

export const Contact = () => (
  <section id="contact" className="relative py-32 sm:py-40 bg-[color:var(--kmg-deep)]" data-testid="contact-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHead eyebrow="09 — Contact" title="Head office in Hillcrest, Durban." />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-16">
        <Reveal className="lg:col-span-5">
          <div className="border border-[rgba(111,227,155,0.18)] bg-[color:var(--kmg-emerald)] p-9 space-y-9">
            <div className="flex gap-4">
              <Building2 size={18} className="text-[color:var(--kmg-lime)] shrink-0 mt-1" />
              <div data-testid="contact-head-office">
                <p className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-[color:var(--kmg-gold)]">Head Office</p>
                {CONTACT.headOffice.map((l) => (
                  <p key={l} className="text-sm mt-1.5 text-[color:var(--kmg-bone-dim)]">{l}</p>
                ))}
                <a
                  href={`tel:${CONTACT.headOfficePhone.replace(/\s/g, "")}`}
                  data-testid="contact-head-office-phone"
                  className="text-sm mt-2.5 block underline-grow text-[color:var(--kmg-lime)]"
                >
                  Tel {CONTACT.headOfficePhone}
                </a>
              </div>
            </div>

            <div className="flex gap-4 pt-8 border-t border-[rgba(244,241,230,0.14)]">
              <MapPin size={18} className="text-[color:var(--kmg-lime)] shrink-0 mt-1" />
              <div data-testid="contact-address">
                <p className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-[color:var(--kmg-gold)]">Farm</p>
                {CONTACT.addressLines.map((l) => (
                  <p key={l} className="text-sm mt-1.5 text-[color:var(--kmg-bone-dim)]">{l}</p>
                ))}
              </div>
            </div>

            <div className="flex gap-4">
              <Phone size={18} className="text-[color:var(--kmg-lime)] shrink-0 mt-1" />
              <div>
                <p className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-[color:var(--kmg-gold)]">Other numbers</p>
                {CONTACT.phones.map((p) => (
                  <a key={p} href={`tel:${p.replace(/\s/g, "")}`} data-testid={`contact-phone-${p.replace(/\s/g, "")}`} className="text-sm mt-1.5 block underline-grow">
                    {p}
                  </a>
                ))}
              </div>
            </div>

            <div className="flex gap-4">
              <Mail size={18} className="text-[color:var(--kmg-lime)] shrink-0 mt-1" />
              <div>
                <p className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-[color:var(--kmg-gold)]">Email</p>
                <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="text-sm mt-1.5 block underline-grow">{CONTACT.email}</a>
              </div>
            </div>

            <div className="flex gap-4">
              <Globe size={18} className="text-[color:var(--kmg-lime)] shrink-0 mt-1" />
              <div>
                <p className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-[color:var(--kmg-gold)]">Website</p>
                <a href={`https://${CONTACT.website}`} target="_blank" rel="noreferrer" data-testid="contact-website" className="text-sm mt-1.5 block underline-grow">
                  {CONTACT.website}
                </a>
              </div>
            </div>

            <div className="flex gap-4 pt-8 border-t border-[rgba(244,241,230,0.14)]">
              <Satellite size={18} className="text-[color:var(--kmg-lime)] shrink-0 mt-1" />
              <div>
                <p className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-[color:var(--kmg-gold)]">Partner HQ</p>
                {CONTACT.sat2farm.map((l) => (
                  <p key={l} className="text-sm mt-1.5 text-[color:var(--kmg-bone-dim)]">{l}</p>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-4 pt-8 border-t border-[rgba(244,241,230,0.14)]">
              {CONTACT.socials.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={s.name}
                  data-testid={`social-${s.name.toLowerCase()}`}
                  className="grid place-items-center w-10 h-10 rounded-full border border-[rgba(111,227,155,0.25)] text-[color:var(--kmg-bone-dim)] hover:text-[color:var(--kmg-lime)] hover:border-[color:var(--kmg-lime)]"
                  style={{ transition: "color .35s ease, border-color .35s ease" }}
                >
                  <Icon name={s.icon} size={16} />
                </a>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.12} className="lg:col-span-7">
          <div className="h-full min-h-[460px] border border-[rgba(111,227,155,0.18)] overflow-hidden">
            <iframe
              title="KMG Farm Head Office — 71 Old Main Road, Hillcrest, Durban"
              src={CONTACT.mapEmbed}
              data-testid="location-map"
              className="w-full h-full min-h-[460px]"
              style={{ border: 0, filter: "grayscale(30%) contrast(1.05)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
